import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircleQuestion, Mic, Send, Loader2, Sparkles, Crown, Lock, ChevronDown, ChevronUp, Type } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import LessonMarkdown from "@/components/LessonMarkdown";
import VoiceDoubtMode from "@/components/VoiceDoubtMode";
import { trackEvent } from "@/lib/posthog";

/**
 * Live Doubt Solver — sidebar chat panel inside the whitepaper Sheet.
 *
 * Pricing model (mirrored on backend in `/api/doubt/ask`):
 *   * Limitless subscribers: unlimited free questions
 *   * Everyone else: 1 credit unlocks a pack of 5 questions. When the
 *     pack empties, the next question costs another credit and refills.
 *
 * Context scope: entire whitepaper (all 3 parts ~3000 words) gets passed
 * to Gemini 3 Flash as system prompt context, so answers stay grounded
 * and on-topic.
 *
 * UX:
 *   * Docks to the right side on desktop (≥1024px), bottom-sheet pattern
 *     on mobile. Collapsed by default — user expands it via the
 *     MessageCircleQuestion button; their preference persists in
 *     sessionStorage for the rest of the reading session.
 *   * Each answer renders markdown, with any trailing "→" follow-up
 *     question highlighted in mint.
 *   * Empty state nudges the user with 3 example questions specific to
 *     the step title they're reading.
 */
export default function DoubtSidebar({ pathId, stepIndex, stepTitle, onCreditsNeeded, onCreditsChange }) {
  const [open, setOpen] = useState(() => {
    try { return sessionStorage.getItem("oak_doubt_sidebar_open") === "1"; } catch { return false; }
  });
  const [status, setStatus] = useState(null);
  const [question, setQuestion] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  // Voice ↔ Text mode toggle. Persisted in localStorage so the user's
  // last choice sticks across reading sessions. Default = text (safer
  // fallback for browsers without SpeechRecognition support).
  const [mode, setMode] = useState(() => {
    if (typeof window === "undefined") return "text";
    try { return window.localStorage.getItem("oak.doubtMode") || "text"; }
    catch { return "text"; }
  });
  const setModePersist = (next) => {
    setMode(next);
    try { window.localStorage.setItem("oak.doubtMode", next); }
    catch { /* ignore quota */ }
    trackEvent("doubt_mode_switched", { mode: next, step_index: stepIndex });
  };

  // Persist sidebar open/collapsed state within the session so switching
  // between steps doesn't keep re-collapsing the panel.
  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      try { sessionStorage.setItem("oak_doubt_sidebar_open", next ? "1" : "0"); } catch {}
      if (next) trackEvent("doubt_sidebar_opened", { step_index: stepIndex });
      return next;
    });
  };

  // Fetch fresh state + conversation history whenever the step changes.
  useEffect(() => {
    if (!pathId || stepIndex == null) return;
    setHistory([]);
    setStatus(null);
    setError(null);
    api.get("/doubt/status", { params: { path_id: pathId, step_index: stepIndex } })
      .then(({ data }) => {
        setStatus(data);
        setHistory(data.history || []);
      })
      .catch((e) => {
        // Silent fail on status endpoint — the ask flow still works
        setError(e?.response?.data?.detail || null);
      });
  }, [pathId, stepIndex]);

  // Auto-scroll to latest answer whenever history grows.
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history.length, busy]);

  // Most recent turn that has an answer (used by VoiceDoubtMode to
  // auto-speak the answer + render the markdown). Re-computed when
  // `history` changes so the voice pane stays in sync.
  const lastTurnRef = useMemo(() => {
    for (let i = history.length - 1; i >= 0; i--) {
      if (history[i]?.answer) return history[i];
    }
    return null;
  }, [history]);

  const ask = async (explicitQ) => {
    const q = (explicitQ ?? question).trim();
    if (!q || busy) return;
    setBusy(true);
    setError(null);
    // Optimistically append the question so the user sees it immediately.
    const turnId = Date.now();
    setHistory((h) => [...h, { id: turnId, question: q, answer: null, created_at: new Date().toISOString() }]);
    setQuestion("");
    try {
      const { data } = await api.post("/doubt/ask", { path_id: pathId, step_index: stepIndex, question: q });
      setHistory((h) =>
        h.map((t) => (t.id === turnId ? { ...t, answer: data.answer } : t))
      );
      setStatus((s) => ({
        ...(s || {}),
        remaining_in_pack: data.remaining_in_pack,
        is_limitless: data.is_limitless,
        balance: data.balance,
      }));
      if (data.credits_charged > 0) {
        toast.info(`1 credit used — ${status?.pack_size ?? 5} questions unlocked`, { duration: 3500 });
      }
      if (typeof data.balance === "number") onCreditsChange?.(data.balance);
      trackEvent("doubt_asked", {
        step_index: stepIndex,
        credits_charged: data.credits_charged,
        is_limitless: data.is_limitless,
      });
    } catch (e) {
      // Remove the optimistic turn since the answer never came.
      setHistory((h) => h.filter((t) => t.id !== turnId));
      const status = e?.response?.status;
      if (status === 402) {
        onCreditsNeeded?.();
      } else {
        const msg = e?.response?.data?.detail || "Couldn't get an answer. Try again.";
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const exampleQuestions = stepTitle
    ? [
        `What's the single most important idea from "${stepTitle}"?`,
        `Give me a concrete example of this in practice.`,
        `What's the most common mistake beginners make here?`,
      ]
    : [];

  const remaining = status?.remaining_in_pack;
  const nextIsCharged = !status?.is_limitless && remaining === 0;

  return (
    <>
      {/* Floating toggle — always visible; expands into the sidebar.
          Stacked ABOVE the OakPad FAB + its "publish your study guide"
          nudge so the two right-rail floating widgets never collide.
          OakPad lives at bottom-24/bottom-8; nudge floats at
          bottom-[9.5rem]/bottom-[5.5rem]; we sit above both. */}
      {!open && (
        <button
          type="button"
          onClick={toggle}
          className="fixed right-4 md:right-8 bottom-[13rem] md:bottom-[8.5rem] z-[60] h-14 w-14 rounded-full btn-primary font-display font-semibold grid place-items-center shadow-2xl hover:scale-105 transition pointer-events-auto"
          data-testid="btn-open-doubt-sidebar"
          aria-label="Open doubt solver"
        >
          <MessageCircleQuestion size={22} />
        </button>
      )}

      {/* Sidebar panel */}
      {open && (
        <aside
          className="fixed z-[60] right-0 inset-y-0 md:top-20 md:bottom-6 md:right-6 w-full md:w-[380px] md:rounded-2xl glass border-l md:border border-white/10 flex flex-col pointer-events-auto animate-fadeup"
          data-testid="doubt-sidebar"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-8 w-8 grid place-items-center rounded-lg bg-[#00FF9D]/15 border border-[#00FF9D]/30 flex-shrink-0">
                <Sparkles size={14} className="text-[#00FF9D]" />
              </div>
              <div className="min-w-0">
                <div className="font-display text-sm truncate">Ask a doubt</div>
                <div className="text-[10px] text-white/50 truncate">
                  About: {stepTitle || "this whitepaper"}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={toggle}
              className="h-8 w-8 grid place-items-center rounded-lg hover:bg-white/5 text-white/60 hover:text-white transition"
              data-testid="btn-close-doubt-sidebar"
              aria-label="Collapse doubt sidebar"
            >
              <ChevronDown size={16} />
            </button>
          </div>

          {/* Status bar — shows remaining questions + Limitless badge */}
          <div className="px-4 py-2 border-b border-white/5 flex items-center justify-between gap-2 text-[11px]">
            {status?.is_limitless ? (
              <span className="inline-flex items-center gap-1 text-[#ffb067]" data-testid="doubt-limitless-badge">
                <Crown size={11} /> Limitless · unlimited questions
              </span>
            ) : remaining == null ? (
              <span className="text-white/45">Checking…</span>
            ) : remaining > 0 ? (
              <span className="text-[#00FF9D]" data-testid="doubt-remaining">
                {remaining} / {status?.pack_size ?? 5} free questions left
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-white/65" data-testid="doubt-next-charged">
                <Lock size={10} /> Next question: 1 credit · unlocks 5 more
              </span>
            )}
            {status && typeof status.balance === "number" && !status.is_limitless && (
              <span className="text-white/40 font-mono-oak">{status.balance} cr</span>
            )}
          </div>

          {/* Voice ↔ Text mode toggle */}
          <div className="px-4 py-2 border-b border-white/5 flex items-center justify-center">
            <div
              role="tablist"
              className="inline-flex rounded-full bg-white/[0.04] border border-white/10 p-0.5 text-[11px] font-display"
              data-testid="doubt-mode-toggle"
            >
              <button
                role="tab"
                aria-selected={mode === "text"}
                onClick={() => setModePersist("text")}
                className={`px-3 py-1 rounded-full inline-flex items-center gap-1 transition ${
                  mode === "text"
                    ? "bg-white/15 text-white"
                    : "text-white/55 hover:text-white/80"
                }`}
                data-testid="btn-doubt-mode-text"
              >
                <Type size={11} /> Text
              </button>
              <button
                role="tab"
                aria-selected={mode === "voice"}
                onClick={() => setModePersist("voice")}
                className={`px-3 py-1 rounded-full inline-flex items-center gap-1 transition ${
                  mode === "voice"
                    ? "bg-[#00FF9D]/15 text-[#9bffcc]"
                    : "text-white/55 hover:text-white/80"
                }`}
                data-testid="btn-doubt-mode-voice"
              >
                <Mic size={11} /> Voice
              </button>
            </div>
          </div>

          {mode === "voice" ? (
            /* Voice-first interface — see VoiceDoubtMode for the
             * Google-Assistant-style mic + transcript + auto-speak flow.
             * We pass `ask()` straight through so it shares the same
             * /doubt/ask endpoint, pricing, and history state. */
            <div className="flex-1 overflow-y-auto" data-testid="doubt-voice-pane">
              <VoiceDoubtMode
                ask={ask}
                busy={busy}
                lastTurn={lastTurnRef}
              />
            </div>
          ) : (
          <>
          {/* Conversation */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="doubt-history">
            {history.length === 0 && !busy ? (
              <div className="text-sm text-white/55" data-testid="doubt-empty-state">
                <p className="mb-3">Ask anything about this whitepaper — I have the full text in context.</p>
                {exampleQuestions.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="label-eyebrow text-[9px]">Try asking</div>
                    {exampleQuestions.map((eq, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => ask(eq)}
                        disabled={busy}
                        className="block w-full text-left px-3 py-2 rounded-lg border border-white/10 hover:border-white/25 hover:bg-white/5 transition text-white/70 text-xs leading-snug"
                        data-testid={`doubt-example-${i}`}
                      >
                        {eq}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              history.map((turn, i) => (
                <DoubtTurn key={turn.id ?? i} turn={turn} />
              ))
            )}
            {busy && history[history.length - 1]?.answer == null && (
              <div className="flex items-center gap-2 text-white/55 text-xs" data-testid="doubt-thinking">
                <Loader2 size={12} className="animate-spin" /> Thinking…
              </div>
            )}
            {error && (
              <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2" data-testid="doubt-error">
                {error}
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); ask(); }}
            className="p-3 border-t border-white/5 flex gap-2"
          >
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={nextIsCharged ? "Ask a doubt · 1 credit unlocks 5" : "Ask a doubt about this whitepaper…"}
              disabled={busy}
              maxLength={500}
              className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/35 focus:border-[#00FF9D]/40 focus:outline-none disabled:opacity-60"
              data-testid="doubt-input"
            />
            <button
              type="submit"
              disabled={busy || !question.trim()}
              className="px-3.5 py-2 rounded-lg btn-primary font-display text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40"
              data-testid="btn-doubt-send"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              Ask
            </button>
          </form>
          </>
          )}
        </aside>
      )}
    </>
  );
}

function DoubtTurn({ turn }) {
  return (
    <div className="space-y-2" data-testid="doubt-turn">
      <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2">
        <div className="label-eyebrow !text-[9px] mb-1 text-white/40">You</div>
        <div className="text-sm text-white/85">{turn.question}</div>
      </div>
      {turn.answer != null && (
        <div className="rounded-lg bg-[#00FF9D]/[0.04] border border-[#00FF9D]/20 px-3 py-2">
          <div className="label-eyebrow !text-[9px] mb-1 text-[#00FF9D]">Oak AI Campus</div>
          <div className="prose-oak prose-sm text-sm text-white/85 leading-relaxed">
            <LessonMarkdown
              markdown={turn.answer}
              components={{
                // Highlight follow-up prompts (lines starting with "→") so
                // the learner's eye is drawn to them.
                p: ({ children }) => {
                  const text = Array.isArray(children) ? children.join("") : String(children);
                  if (typeof text === "string" && text.trim().startsWith("→")) {
                    return <p className="mt-2 text-[#00FF9D]/90 border-l-2 border-[#00FF9D]/40 pl-3 italic">{children}</p>;
                  }
                  return <p className="my-2">{children}</p>;
                },
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
